import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { DatePicker as CustomDatePicker } from '../../../components/dateTimePickers';
import {
  editUserClaim,
  getCompanyClaimTypesWithStats,
  getUsersClaims
} from '../../../api/Payroll/claim';
import { UserClaimInput, UserClaimStatus, UserClaimView, CompanyClaimTypeView } from '../../../models/payroll';
import { GetCompanyRates } from '../../../api/General/currency';
import { CompanyCurrencyRate, Currency } from '../../../models/general';
import { getCompanyRules } from '../../../api/General/Company';
import { formatDate, formatNumber } from '../../../utils';
import CurrencyDropdown from '../../../components/currencyDropdown';
import Pagination from '../../../components/Pagination';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Alert, AlertDescription } from '../../../components/ui/Alert';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';

const PAGE_SIZE = 10;

const Claims: React.FC = () => {
  const { t } = useTranslation();
  const company = useSelector((s: any) => s.user.company);
  const CompanyCurrencyRate = useSelector((s: any) => s.user.CompanyCurrencyRate);
  const { toast, showToast, dismissToast } = useToast();

  // Data
  const [claimTypes, setClaimTypes] = useState<CompanyClaimTypeView[]>([]);
  const [allClaims, setAllClaims] = useState<UserClaimView[]>([]);
  const [claims, setClaims] = useState<UserClaimView[]>([]);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'All' | 'Approved' | 'Rejected' | 'Pending' | 'Paid' | 'PendingPayment'>('All');
  const [employeeName, setEmployeeName] = useState('');
  const [expenseTypeIds, setExpenseTypeIds] = useState<number[]>([]);
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);
  const [onlyCurrentMonth, setOnlyCurrentMonth] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);

  // Selection / actions
  const [checkedRows, setCheckedRows] = useState<Record<number, boolean>>({});
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [reasonOfRejection, setReasonOfRejection] = useState('');
  const [warningOnRejectText, setWarningOnRejectText] = useState<string | null>(null);

  // Currency & totals
  const [rates, setRates] = useState<CompanyCurrencyRate[]>([]);
  const [baseCurrency, setBaseCurrency] = useState<Currency | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<any>(0);

  // UI state
  const [loading, setLoading] = useState(false);

  // Derived totals
  const { totalAmount, totalApprovedAmount, totalAmountOfTheMonth, totalApprovedAmountOfTheMonth } = useMemo(() => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    let total = 0, totalApproved = 0, totalMonth = 0, totalApprovedMonth = 0;
    allClaims.forEach(c => {
      const amount = parseFloat(c.amount);
      total += amount;
      if ([UserClaimStatus.Approved, UserClaimStatus.Paid, UserClaimStatus.PendingPayment].includes(c.status)) {
        totalApproved += amount;
        if (c.paymentDate) {
          const d = new Date(c.paymentDate);
          if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
            totalApprovedMonth += amount;
          }
        }
      }
      if (c.requestDate) {
        const d = new Date(c.requestDate);
        if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
          totalMonth += amount;
        }
      }
    });
    return {
      totalAmount: total,
      totalApprovedAmount: totalApproved,
      totalAmountOfTheMonth: totalMonth,
      totalApprovedAmountOfTheMonth: totalApprovedMonth
    };
  }, [allClaims]);

  // Load data
  const loadData = useCallback(async (currencyId?: any) => {
    setLoading(true);
    try {
      const [typesResp, claimsResp, ratesResp, rulesResp] = await Promise.all([
        getCompanyClaimTypesWithStats(),
        getUsersClaims(currencyId ?? 0),
        GetCompanyRates(),
        getCompanyRules(company?.id)
      ]);
      setClaimTypes(typesResp.payload || []);
      setAllClaims(claimsResp.payload || []);
      setClaims(claimsResp.payload || []);
      const baseCurr: CompanyCurrencyRate = {
        id: 0,
        baseCurrency: rulesResp.payload.currency,
        baseCurrencyId: rulesResp.payload.currency.id,
        foreignCurrency: rulesResp.payload.currency,
        foreignCurrencyId: 0,
        company: company,
        companyId: company?.id,
        rate: 0,
        createdOn: new Date(),
        isArchived: false
      };
      const r: CompanyCurrencyRate[] = [baseCurr, ...ratesResp.payload];
      setRates(r);
      setBaseCurrency(rulesResp.payload.currency);
    } catch (err) {
      console.log(err);
      showToast(t('Failed to load claims'), 'error');
    } finally {
      setLoading(false);
    }
  }, [company, t, showToast]);

  useEffect(() => { loadData(CompanyCurrencyRate?.id); }, [loadData, CompanyCurrencyRate]);

  // Filtering logic
  const applyFilters = useCallback(() => {
    dayjs.extend(isSameOrBefore); dayjs.extend(isSameOrAfter);
    const lowerName = employeeName.trim().toLowerCase();
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const filtered = allClaims.filter(c => {
      const statusMatch = statusFilter === 'All' || c.status === statusFilter;
      const employeeMatch = !lowerName || (c.user?.fullName?.toLowerCase().includes(lowerName));
      const expenseMatch = expenseTypeIds.length === 0 || expenseTypeIds.includes(c.companyClaimTypeId || 0);
      const reqDate = c.requestDate ? dayjs(c.requestDate) : null;
      const monthMatch = onlyCurrentMonth ? (reqDate && reqDate.month() === currentMonth && reqDate.year() === currentYear) : true;
      const fromMatch = fromDate ? (reqDate && reqDate.isSameOrAfter(dayjs(fromDate).startOf('day'))) : true;
      const toMatch = toDate ? (reqDate && reqDate.isSameOrBefore(dayjs(toDate).endOf('day'))) : true;
      return statusMatch && employeeMatch && expenseMatch && monthMatch && fromMatch && toMatch;
    });
    setClaims(filtered);
    setPage(1);
  }, [allClaims, statusFilter, employeeName, expenseTypeIds, onlyCurrentMonth, fromDate, toDate]);

  useEffect(() => { applyFilters(); }, [applyFilters]);

  // Pagination slice
  const pagedClaims = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return claims.slice(start, start + PAGE_SIZE);
  }, [claims, page]);

  const totalPages = Math.max(1, Math.ceil(claims.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  // Selection
  const toggleRow = (id: number) => { setCheckedRows(r => ({ ...r, [id]: !r[id] })); };
  const clearSelection = () => setCheckedRows({});
  const selectedIds = useMemo(() => Object.entries(checkedRows).filter(([, v]) => v).map(([k]) => Number(k)), [checkedRows]);

  // Actions
  const updateClaimStatus = async (approve: boolean) => {
    if (selectedIds.length === 0) { showToast(t('Please select a claim'), 'error'); return; }
    setLoading(true);
    try {
      for (const id of selectedIds) {
        const claim = allClaims.find(c => c.id === id);
        if (!claim) continue;
        const input: UserClaimInput = { id: claim.id, status: approve ? UserClaimStatus.Approved : UserClaimStatus.Rejected, managerComment: reasonOfRejection } as any;
        await editUserClaim(claim.id, input);
      }
      clearSelection();
      setReasonOfRejection('');
      showToast(approve ? t('Claims approved') : t('Claims updated'), 'success');
      loadData(CompanyCurrencyRate?.id);
    } catch (err: any) {
      console.log(err);
      showToast(t('Failed to update claims'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const openReject = () => {
    if (selectedIds.length === 0) { showToast(t('Please select a claim'), 'error'); return; }
    const hasPendingPayment = allClaims.some(c => selectedIds.includes(c.id) && c.status === UserClaimStatus.PendingPayment);
    if (hasPendingPayment) {
      setWarningOnRejectText(t('The claims selected are already part of a monthly payroll. Are you sure you want to remove them from the monthly payroll?'));
    } else {
      setWarningOnRejectText(null);
    }
    setOpenRejectDialog(true);
  };

  const closeReject = () => { setOpenRejectDialog(false); setReasonOfRejection(''); setWarningOnRejectText(null); };

  const handleCurrencyChange = (option: any) => {
    setSelectedCurrency(option);
    loadData(option?.foreignCurrencyId);
  };

  const currentCurrencyCode = selectedCurrency?.foreignCurrency?.currencyCode || baseCurrency?.currencyCode || '';

  const formatMoney = (n: number) => formatNumber(n) + ' ' + currentCurrencyCode;

  const statusBadgeVariant = (status: UserClaimStatus) => {
    switch (status) {
      case UserClaimStatus.Approved: return 'success' as const;
      case UserClaimStatus.Rejected: return 'error' as const;
      case UserClaimStatus.Pending: return 'warning' as const;
      case UserClaimStatus.Paid: return 'info' as const;
      case UserClaimStatus.PendingPayment: return 'info' as const;
      default: return 'neutral' as const;
    }
  };

  return (
    <PageLayout
      title={t('Claims Management')}
      icon={ClipboardList}
      accentColor="emerald"
      maxWidth="1600px"
      headerActions={
        <div className="flex flex-wrap gap-3 items-center">
          <Button
            variant="outline"
            disabled={selectedIds.length === 0}
            onClick={() => updateClaimStatus(true)}
            data-testid="approve-btn"
          >
            {t('leaves:Approve')}
          </Button>
          <Button
            variant="outline"
            disabled={selectedIds.length === 0}
            onClick={openReject}
            data-testid="reject-btn"
          >
            {t('leaves:Reject')}
          </Button>
        </div>
      }
    >
    <div className="relative flex flex-col gap-6 flex-1">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          iconName={showFilters ? 'X' : 'Filter'}
          onClick={() => setShowFilters(s => !s)}
        >
          {showFilters ? t('Hide Filters') : t('Filter')}
        </Button>
        <CurrencyDropdown value={selectedCurrency} options={rates} onOptionChange={handleCurrencyChange} />
        <Checkbox
          checked={onlyCurrentMonth}
          onChange={() => setOnlyCurrentMonth(c => !c)}
          label={t('Show current month claims')}
        />
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="transition-all duration-200 rounded-xl border border-border bg-card p-4 flex flex-wrap gap-6">
          <div className="flex flex-col gap-2" data-testid="claim-status-filter">
            <span className="text-sm font-semibold text-foreground">{t('Status')}</span>
            <div className="flex items-center gap-4 flex-wrap">
              {['All', 'Approved', 'Rejected', 'Pending', 'Paid', 'PendingPayment'].map(s => (
                <label key={s} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="statusFilter"
                    value={s}
                    checked={statusFilter === s}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t(s)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Input
              label={t('Employee Name')}
              value={employeeName}
              onChange={(e: any) => setEmployeeName(e.target.value)}
              className="min-w-[220px]"
            />
          </div>
          <div className="flex flex-col gap-2 min-w-[240px]">
            <Select
              multiple
              searchable
              options={claimTypes.map(x => ({ value: x.id, label: x.name }))}
              value={expenseTypeIds}
              onChange={(vals: any) => setExpenseTypeIds(vals)}
              label={t('Claim Type')}
              placeholder={t('Select')}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-foreground">{t('From Date')}</span>
            <CustomDatePicker label={t('From Date')} size="small" value={fromDate ?? null} onChange={(d: any) => setFromDate(d)} fullWidth={false} />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-foreground">{t('To Date')}</span>
            <CustomDatePicker label={t('To Date')} size="small" value={toDate ?? null} onChange={(d: any) => setToDate(d)} fullWidth={false} />
          </div>
          <div className="flex items-end">
            <Button
              variant="ghost"
              onClick={() => {
                setStatusFilter('All');
                setEmployeeName('');
                setExpenseTypeIds([]);
                setFromDate(null);
                setToDate(null);
                setOnlyCurrentMonth(false);
              }}
            >
              {t('Reset')}
            </Button>
          </div>
        </div>
      )}

      {/* Summary Bar */}
      <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap gap-3" data-testid="claims-summary">
        <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
          {t('Total Amount')}: {formatMoney(totalAmount)}
        </span>
        <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
          {t('Total Approved Amount')}: {formatMoney(totalApprovedAmount)}
        </span>
        <span className="inline-flex items-center rounded-full border border-blue-500/30 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
          {t('Total Amount For This Month')}: {formatMoney(totalAmountOfTheMonth)}
        </span>
        <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
          {t('Total Approved Amount For This Month')}: {formatMoney(totalApprovedAmountOfTheMonth)}
        </span>
        <span className="inline-flex items-center rounded-full border border-border bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
          {t('Showing')} {pagedClaims.length} {t('of')} {claims.length}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="claims-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[90px]">{t('Claim ID')}</TableHead>
              <TableHead>{t('Employee Name')}</TableHead>
              <TableHead>{t('Claim Type')}</TableHead>
              <TableHead>{t('Status')}</TableHead>
              <TableHead>{t('Amount')}</TableHead>
              <TableHead>{t('Claim Date')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedClaims.map(row => (
              <TableRow key={row.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      size="sm"
                      checked={!!checkedRows[row.id]}
                      onChange={() => toggleRow(row.id)}
                    />
                    <Link to={`/payroll/claims/${row.id}`} className="font-semibold underline text-primary hover:text-primary/80">
                      {row.id}
                    </Link>
                  </div>
                </TableCell>
                <TableCell>{row.user?.fullName || ''}</TableCell>
                <TableCell>{row.companyClaimType?.name || ''}</TableCell>
                <TableCell>
                  <StatusBadge variant={statusBadgeVariant(row.status)} size="sm">
                    {t(row.status)}
                  </StatusBadge>
                </TableCell>
                <TableCell className="font-medium">{formatNumber(parseFloat(row.amount))} {row.currency?.currencyCode || ''}</TableCell>
                <TableCell>{formatDate(new Date(row.requestDate))}</TableCell>
              </TableRow>
            ))}
            {!loading && pagedClaims.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className="flex items-center justify-center py-8 opacity-70">
                    <span className="text-sm text-muted-foreground">{t('No claims match your filters')}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="md" />
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {claims.length > PAGE_SIZE && (
        <div className="flex justify-end">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onOpenChange={(open) => { if (!open) closeReject(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('REASON OF REJECTION')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            {warningOnRejectText && (
              <Alert variant="warning">
                <AlertDescription>{warningOnRejectText}</AlertDescription>
              </Alert>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Reason Of Rejection')}</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px]"
                value={reasonOfRejection}
                onChange={e => setReasonOfRejection(e.target.value)}
                rows={3}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="default" onClick={() => { updateClaimStatus(false); closeReject(); }}>
              {t('Submit')}
            </Button>
            <Button variant="outline" onClick={closeReject}>
              {t('Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/55">
          <Spinner size="lg" />
        </div>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
    </PageLayout>
  );
};

export default Claims;
